/**
 * The user's own Rithmic login (ADR-0027): trading-socket messages to connect / disconnect it and
 * the words the Settings panel shows for its state. Pure; only type-imports generated code.
 *
 * Connecting never carries the password: the gateway resolves the stored, encrypted credentials
 * by `login_id` for the socket's own user. Nothing here retries: the gateway alone tries a refused
 * login again (bounded, `LoginStatus.retry`, owner decision 2026-10-04); a kicked login waits for
 * the user (SPEC §6.4).
 */
import type { RithmicLogin, RithmicLogout } from "../gen/helm/v1/session_pb";

/** Mirrors proto `LoginConnState` (checked against the generated enum in mirrors.ts). */
export const LOGIN_STATE = {
  UNSPECIFIED: 0,
  DISCONNECTED: 1,
  CONNECTING: 2,
  CONNECTED: 3,
  REJECTED: 4,
  KICKED: 5,
} as const;

export function rithmicLoginMessage(loginId: string): RithmicLogin {
  return { $typeName: "helm.v1.RithmicLogin", loginId, password: "" };
}

export function rithmicLogoutMessage(loginId: string): RithmicLogout {
  return { $typeName: "helm.v1.RithmicLogout", loginId };
}

/** One line for a login's connection state, with Rithmic's own text where it gave one. */
export function loginStateText(s: { state: number; detail: string } | null): string {
  if (s === null) return "Not connected";
  const detail = s.detail ? `: ${s.detail}` : "";
  switch (s.state) {
    case LOGIN_STATE.CONNECTING:
      return "Connecting…";
    case LOGIN_STATE.CONNECTED:
      return "Connected";
    case LOGIN_STATE.REJECTED:
      return `Rejected by Rithmic${detail}`;
    case LOGIN_STATE.KICKED:
      return `Logged out by Rithmic${detail}`;
    case LOGIN_STATE.DISCONNECTED:
      return `Disconnected${s.detail && s.detail !== "Disconnected" ? detail : ""}`;
    default:
      return "Not connected";
  }
}

/** Mirrors proto `LoginRetryState` (checked against the generated enum in mirrors.ts). */
export const LOGIN_RETRY = {
  UNSPECIFIED: 0,
  RETRYING: 1,
  GAVE_UP: 2,
  NOT_RETRIED: 3,
  STOPPED: 4,
} as const;

/** The `LoginStatus` fields the retry line reads (`retry` is absent once connected). */
export type LoginRetryInput = {
  loginId: string;
  retry?:
    | {
        state: number;
        attempt: number;
        maxAttempts: number;
        nextAtMs: bigint;
        reason: string;
        why: string;
      }
    | undefined;
} | null;

/** The red one-line state of a refused login (status line, Rithmic login dialog). */
export type LoginRetryLine = {
  /** "Rithmic refused your login (13 permission denied) — retrying in 30 s, try 2 of 5". */
  text: string;
  /** The same in a few words, where the line is short of room: "Refused — retry in 30 s, 2 of 5". */
  short: string;
  /** What the line offers: stop the scheduled retry, or log in again (the Rithmic login dialog). */
  action: "stop" | "login";
  /** RETRYING: unix ms of the next try (the one countdown); `null` otherwise. */
  nextAtMs: number | null;
  loginId: string;
};

/**
 * The gateway's automatic retry of a refused login, as one line (owner decision 2026-10-04):
 * only a transient refusal is tried again, at most `maxAttempts` tries in all; a credential
 * refusal never is. `nowMs` only words the countdown, so a caller can re-word the same line every
 * second without anything else changing; with `at` the next try is named by its time instead
 * (a place without a ticking timer). `null` without a refusal.
 */
export function loginRetryLine(
  s: LoginRetryInput,
  nowMs: number,
  at?: (ms: number) => string,
): LoginRetryLine | null {
  const r = s?.retry;
  if (!s || !r || r.state === LOGIN_RETRY.UNSPECIFIED) return null;
  const refused = `Rithmic refused your login (${r.reason})`;
  const line = (text: string, short: string, action: "stop" | "login", nextAtMs: number | null) => ({
    text,
    short,
    action,
    nextAtMs,
    loginId: s.loginId,
  });
  switch (r.state) {
    case LOGIN_RETRY.RETRYING: {
      const nextAtMs = Number(r.nextAtMs);
      const secs = Math.max(0, Math.ceil((nextAtMs - nowMs) / 1000));
      const soon = secs > 0 ? `in ${String(secs)} s` : "now";
      const when = at ? `retrying at ${at(nextAtMs)}` : `retrying ${soon}`;
      const of = `${String(r.attempt)} of ${String(r.maxAttempts)}`;
      return line(
        `${refused} — ${when}, try ${of}`,
        `Refused — retry ${at ? `at ${at(nextAtMs)}` : soon}, ${of}`,
        "stop",
        nextAtMs,
      );
    }
    case LOGIN_RETRY.GAVE_UP:
      return line(
        `${refused} — gave up after ${String(r.attempt)} tries`,
        `Refused — gave up after ${String(r.attempt)} tries`,
        "login",
        null,
      );
    case LOGIN_RETRY.NOT_RETRIED:
      return r.why === "credential"
        ? line(
            "Rithmic rejected the password — not retried",
            "Password rejected — not retried",
            "login",
            null,
          )
        : line(`${refused} — not retried`, "Refused — not retried", "login", null);
    case LOGIN_RETRY.STOPPED:
      return line(`${refused} — retrying stopped`, "Refused — retrying stopped", "login", null);
    default:
      return null;
  }
}

/** Badge text for a stored login's environment (`rithmic_logins.environment`). */
export function loginEnvironmentLabel(env: string): string {
  switch (env) {
    case "test":
      return "TEST";
    case "paper":
      return "PAPER / PROP";
    case "live":
      return "LIVE";
    default:
      return env.toUpperCase();
  }
}

/** Does the owner switch apply to a login stored with this environment? */
export function loginNeedsSwitch(env: string, systemName: string): boolean {
  return env !== "test" || systemName !== "Rithmic Test";
}

/** The typed confirmation must equal the login's user name exactly (case-sensitive, like Rithmic). */
export function confirmationMatches(typed: string, username: string): boolean {
  return typed === username && username.length > 0;
}
